
import React, { useState, useEffect } from 'react';
import { ActivityLog, Transaction, Suggestion, Individual, Business } from '../types';
import { 
  LogOut, Activity, DollarSign, Users, Shield, TrendingUp, Building2, 
  User, CreditCard, Banknote, Download, Search, PieChart as PieChartIcon, 
  ArrowUpRight, ArrowDownRight, Filter, Briefcase, Globe, MessageCircle, 
  CheckCircle2, Clock, Trash2, ShieldAlert, Settings as SettingsIcon, 
  BellRing, Save, RefreshCw, MoreVertical, Ban, Mail, Send, Plus, List
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Logo from './Logo';

interface OwnerPortalProps {
  onLogout: () => void;
  userName: string;
  activities: ActivityLog[];
  transactions: Transaction[];
  suggestions: Suggestion[];
  onUpdateSuggestionStatus: (id: string, status: 'new' | 'reviewed') => void;
  usersCount: number;
  individuals: Individual[];
  businesses: Business[];
  onDeleteIndividual: (id: string) => void;
  onDeleteBusiness: (id: string) => void;
  globalServices: string[];
  onAddGlobalService: (name: string) => void;
  pricing: { individual: number, business: number };
  onUpdatePricing: (pricing: { individual: number, business: number }) => void;
}

const OwnerPortal: React.FC<OwnerPortalProps> = ({ 
  onLogout, 
  userName, 
  activities, 
  transactions,
  suggestions,
  onUpdateSuggestionStatus,
  usersCount,
  individuals,
  businesses,
  onDeleteIndividual,
  onDeleteBusiness,
  globalServices,
  onAddGlobalService,
  pricing,
  onUpdatePricing
}) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'activity' | 'financials' | 'suggestions' | 'settings'>('dashboard');
  const [activityFilter, setActivityFilter] = useState<'all' | 'business' | 'individual' | 'owner'>('all');
  const [userSearch, setUserSearch] = useState('');
  
  // Settings state
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [globalPricing, setGlobalPricing] = useState(pricing);
  const [newServiceName, setNewServiceName] = useState('');

  // Synchronize local state if prop changes
  useEffect(() => {
    setGlobalPricing(pricing);
  }, [pricing]);

  // Derived Stats
  const businessUsersCount = businesses.length;
  const individualUsersCount = individuals.length;
  const totalUserCount = businessUsersCount + individualUsersCount;

  const totalRevenue = transactions.reduce((acc, curr) => acc + curr.amount, 0);
  const businessRevenue = transactions.filter(t => t.userRole === 'business').reduce((acc, t) => acc + t.amount, 0);
  const individualRevenue = transactions.filter(t => t.userRole === 'individual').reduce((acc, t) => acc + t.amount, 0);

  const revenueData = [
    { name: 'Business Subscriptions', value: businessRevenue, color: '#ec4899' },
    { name: 'Inquiry Fees', value: individualRevenue, color: '#9333ea' },
  ];

  const filteredActivities = activities.filter(act => activityFilter === 'all' || act.userRole === activityFilter);
  
  const filteredUsers = [
    ...businesses.map(b => ({ ...b, type: 'business' })),
    ...individuals.map(i => ({ ...i, type: 'individual' }))
  ].filter(u => u.name.toLowerCase().includes(userSearch.toLowerCase()) || (u as any).email.toLowerCase().includes(userSearch.toLowerCase()));

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, colorClass }: any) => (
    <div className="bg-gray-800 border border-gray-700 p-6 rounded-2xl shadow-lg relative overflow-hidden group hover:border-gray-600 transition-all">
       <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
          <Icon className={`w-20 h-20 ${colorClass}`} />
       </div>
       <div className="relative z-10">
          <h3 className="text-gray-400 font-medium text-sm mb-1">{title}</h3>
          <p className="text-3xl font-bold text-white mb-2">{value}</p>
          <div className="flex items-center text-xs">
             {trend === 'up' ? (
                <ArrowUpRight className="w-3 h-3 text-green-400 mr-1" />
             ) : (
                <ArrowDownRight className="w-3 h-3 text-gray-400 mr-1" />
             )}
             <span className={trend === 'up' ? 'text-green-400' : 'text-gray-400'}>{subtitle}</span>
          </div>
       </div>
    </div>
  );

  const handleBroadcast = (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;
    alert(`Global Broadcast Sent to ${totalUserCount} users: "${broadcastMessage}"`);
    setBroadcastMessage('');
  };

  const handleAddService = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName.trim()) return;
    onAddGlobalService(newServiceName);
    setNewServiceName('');
    alert(`Service "${newServiceName}" added to the platform catalog.`);
  };

  const handleCommitPricing = () => {
    onUpdatePricing(globalPricing);
    alert('Platform pricing model updated globally.');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 font-sans flex flex-col">
      {/* Top Navigation */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-20 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="p-2.5 bg-gray-900 rounded-xl border border-gray-700 text-wedding-500">
               <Logo className="w-8 h-8" invert />
            </div>
            <div>
               <h1 className="font-serif font-black text-2xl text-white tracking-tight">ADMIN COMMAND</h1>
               <div className="flex items-center text-[10px] text-wedding-500 font-bold uppercase tracking-widest mt-0.5">
                  <Shield className="w-3 h-3 mr-1" />
                  Privileged Access Level 1
               </div>
            </div>
          </div>
          <div className="flex items-center space-x-6">
             <div className="hidden lg:flex flex-col items-end">
                <span className="text-sm font-bold text-white">{userName}</span>
                <span className="text-xs text-gray-500">System Administrator</span>
             </div>
             <div className="h-8 w-px bg-gray-700"></div>
             <button 
                onClick={onLogout}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700/50 rounded-xl transition-all"
                title="Secure Logout"
             >
                <LogOut className="w-6 h-6" />
             </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-7xl mx-auto w-full pt-8 px-4 gap-8">
        
        {/* Sidebar Nav */}
        <nav className="w-64 flex-shrink-0 hidden md:block space-y-2">
            <button
               onClick={() => setActiveTab('dashboard')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'dashboard' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <TrendingUp className="w-5 h-5 mr-3" />
                Dashboard
            </button>
            <button
               onClick={() => setActiveTab('users')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'users' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <Users className="w-5 h-5 mr-3" />
                User Directory
            </button>
            <button
               onClick={() => setActiveTab('activity')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'activity' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <Activity className="w-5 h-5 mr-3" />
                Audit Logs
            </button>
            <button
               onClick={() => setActiveTab('financials')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'financials' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <DollarSign className="w-5 h-5 mr-3" />
                Financial Ledger
            </button>
            <button
               onClick={() => setActiveTab('suggestions')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'suggestions' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <MessageCircle className="w-5 h-5 mr-3" />
                Feedback Inbox
                {suggestions.filter(s => s.status === 'new').length > 0 && (
                  <span className="ml-auto w-5 h-5 flex items-center justify-center bg-wedding-600 text-white text-[10px] rounded-full">
                    {suggestions.filter(s => s.status === 'new').length}
                  </span>
                )}
            </button>
            <div className="pt-6 pb-2 px-4">
               <span className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">Global Config</span>
            </div>
            <button
               onClick={() => setActiveTab('settings')}
               className={`w-full flex items-center px-4 py-3.5 rounded-2xl transition-all border ${
                   activeTab === 'settings' ? 'bg-wedding-900/20 text-wedding-50 border-wedding-900/50 shadow-lg font-bold' : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800'
               }`}
            >
                <SettingsIcon className="w-5 h-5 mr-3" />
                System Settings
            </button>
        </nav>

        {/* Main Content Area */}
        <main className="flex-1 pb-12 overflow-hidden">
            {activeTab === 'dashboard' && (
                <div className="space-y-8 animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <StatCard 
                          title="Total Revenue" 
                          value={`$${totalRevenue.toLocaleString()}`} 
                          subtitle="+8.2% vs prev period" 
                          trend="up"
                          icon={Banknote} 
                          colorClass="text-green-500" 
                        />
                        <StatCard 
                          title="Registered Users" 
                          value={totalUserCount} 
                          subtitle={`${businessUsersCount} BIZ • ${individualUsersCount} IND`} 
                          trend="up"
                          icon={Users} 
                          colorClass="text-blue-500" 
                        />
                        <StatCard 
                          title="Total Actions" 
                          value={activities.length} 
                          subtitle="Ops efficiency 98%" 
                          trend="up"
                          icon={Activity} 
                          colorClass="text-purple-500" 
                        />
                        <StatCard 
                          title="Avg. Deal Size" 
                          value={`$${(totalRevenue / (transactions.length || 1)).toFixed(0)}`} 
                          subtitle="Stabilized" 
                          trend="down"
                          icon={TrendingUp} 
                          colorClass="text-wedding-500" 
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                       <div className="lg:col-span-2 bg-gray-800 border border-gray-700 p-8 rounded-2xl shadow-xl">
                          <h3 className="text-xl font-bold mb-8 flex items-center">
                             <PieChartIcon className="w-5 h-5 mr-3 text-wedding-500" />
                             Composition
                          </h3>
                          <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={revenueData} layout="vertical">
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={150} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                <Tooltip 
                                  contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px' }}
                                  cursor={{ fill: 'transparent' }}
                                />
                                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                   {revenueData.map((entry, index) => (
                                     <Cell key={`cell-${index}`} fill={entry.color} />
                                   ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                       </div>

                       <div className="bg-gray-800 border border-gray-700 p-8 rounded-2xl shadow-xl flex flex-col">
                          <h3 className="text-xl font-bold mb-6 flex items-center">
                             <ShieldAlert className="w-5 h-5 mr-3 text-yellow-500" />
                             Critical Alerts
                          </h3>
                          <div className="flex-1 space-y-4">
                             <div className="p-4 bg-red-900/20 border border-red-900/50 rounded-xl">
                                <p className="text-xs font-bold text-red-400 uppercase tracking-widest mb-1">Security</p>
                                <p className="text-sm text-gray-300">Unauthorized login attempt from IP 192.168.1.1 blocked.</p>
                             </div>
                             <div className="p-4 bg-yellow-900/20 border border-yellow-900/50 rounded-xl">
                                <p className="text-xs font-bold text-yellow-400 uppercase tracking-widest mb-1">Performance</p>
                                <p className="text-sm text-gray-300">API latency spike detected.</p>
                             </div>
                             <div className="p-4 bg-blue-900/20 border border-blue-900/50 rounded-xl">
                                <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">Update</p>
                                <p className="text-sm text-gray-300">V2.4.0 rollout scheduled.</p>
                             </div>
                          </div>
                       </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && (
                <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden animate-fadeIn h-full flex flex-col">
                    <div className="p-8 border-b border-gray-700">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-1">User Directory</h2>
                                <p className="text-gray-500 text-sm">Managing {totalUserCount} accounts.</p>
                            </div>
                            <div className="relative w-80">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                                <input 
                                    type="text" 
                                    placeholder="Search users..." 
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-wedding-500 outline-none"
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900/50 text-gray-400 font-bold uppercase text-[10px] tracking-widest sticky top-0">
                                <tr>
                                    <th className="px-8 py-5">Account</th>
                                    <th className="px-8 py-5">Type</th>
                                    <th className="px-8 py-5">Location</th>
                                    <th className="px-8 py-5 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/50">
                                {filteredUsers.map((u: any) => (
                                    <tr key={u.id} className="hover:bg-gray-700/30 transition-all group">
                                        <td className="px-8 py-5">
                                           <div className="flex items-center">
                                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 font-bold ${u.type === 'business' ? 'bg-wedding-900/30 text-wedding-400' : 'bg-blue-900/30 text-blue-400'}`}>
                                                 {u.name.charAt(0)}
                                              </div>
                                              <div>
                                                 <p className="text-white font-bold">{u.name}</p>
                                                 <p className="text-xs text-gray-500">{u.email || u.phone}</p>
                                              </div>
                                           </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-tighter ${
                                                u.type === 'business' ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-900' : 'bg-blue-900/40 text-blue-400 border border-blue-900'
                                            }`}>
                                                {u.type}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-gray-400">
                                            {u.city}, {u.country}
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                           <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white" title="Send Direct Email">
                                                 <Mail className="w-4 h-4" />
                                              </button>
                                              <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-yellow-400" title="Block User">
                                                 <Ban className="w-4 h-4" />
                                              </button>
                                              <button 
                                                onClick={() => u.type === 'business' ? onDeleteBusiness(u.id) : onDeleteIndividual(u.id)}
                                                className="p-2 hover:bg-red-900/20 rounded-lg text-gray-400 hover:text-red-500" 
                                                title="Delete Account"
                                              >
                                                 <Trash2 className="w-4 h-4" />
                                              </button>
                                           </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredUsers.length === 0 && (
                            <div className="p-20 text-center flex flex-col items-center">
                                <Users className="w-12 h-12 text-gray-700 mb-4" />
                                <h4 className="text-gray-500 font-bold uppercase tracking-widest text-xs">No users matched your search</h4>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'activity' && (
                <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden animate-fadeIn h-full flex flex-col">
                    <div className="p-8 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-1">Audit Stream</h2>
                            <p className="text-gray-500 text-sm">Forensic records for all system events.</p>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-900 p-1.5 rounded-xl border border-gray-700">
                           {(['all', 'business', 'individual', 'owner'] as const).map(role => (
                             <button
                               key={role}
                               onClick={() => setActivityFilter(role)}
                               className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all ${activityFilter === role ? 'bg-wedding-600 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
                             >
                               {role}
                             </button>
                           ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900/50 text-gray-400 font-bold uppercase text-[10px] tracking-widest sticky top-0">
                                <tr>
                                    <th className="px-8 py-5">User</th>
                                    <th className="px-8 py-5">Action</th>
                                    <th className="px-8 py-5 text-right">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/50">
                                {filteredActivities.map((act) => (
                                    <tr key={act.id} className="hover:bg-gray-700/30 transition-all group">
                                        <td className="px-8 py-5">
                                           <div>
                                              <p className="text-white font-medium group-hover:text-wedding-400 transition-colors">{act.userName}</p>
                                              <p className="text-[10px] text-gray-500 uppercase tracking-tighter">{act.userRole}</p>
                                           </div>
                                        </td>
                                        <td className="px-8 py-5 text-gray-300">
                                            {act.description}
                                        </td>
                                        <td className="px-8 py-5 text-right text-gray-500 font-mono text-xs">
                                            {new Date(act.timestamp).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'financials' && (
                <div className="space-y-8 animate-fadeIn">
                   <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden">
                      <div className="p-8 border-b border-gray-700 flex justify-between items-center">
                         <div>
                            <h2 className="text-2xl font-bold text-white mb-1">Financial Ledger</h2>
                            <p className="text-gray-500 text-sm">Master record of all platform revenue.</p>
                         </div>
                         <button className="flex items-center px-4 py-2 bg-gray-900 border border-gray-700 rounded-xl hover:bg-gray-800 transition-all text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <Download className="w-4 h-4 mr-2" />
                            Export Ledger
                         </button>
                      </div>
                      <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-gray-400">
                                <thead className="bg-gray-900 text-gray-200 uppercase text-[10px] tracking-widest">
                                    <tr>
                                        <th className="px-8 py-5">Tx ID</th>
                                        <th className="px-8 py-5">Account</th>
                                        <th className="px-8 py-5">Amount</th>
                                        <th className="px-8 py-5">Type</th>
                                        <th className="px-8 py-5 text-right">Processing Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700">
                                    {transactions.map((t) => (
                                        <tr key={t.id} className="hover:bg-gray-700/30 transition-colors">
                                            <td className="px-8 py-5 font-mono text-[10px] text-gray-600">#{t.id.slice(0, 8)}</td>
                                            <td className="px-8 py-5 text-white font-bold">{t.userName}</td>
                                            <td className="px-8 py-5 text-green-400 font-black text-base">+{t.amount.toLocaleString()} {t.currency}</td>
                                            <td className="px-8 py-5 text-xs uppercase tracking-widest font-bold text-gray-500">{t.userRole}</td>
                                            <td className="px-8 py-5 text-right font-mono text-[10px]">{new Date(t.timestamp).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                   </div>
                </div>
            )}

            {activeTab === 'suggestions' && (
                <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden animate-fadeIn h-full flex flex-col">
                    <div className="p-8 border-b border-gray-700">
                        <h2 className="text-2xl font-bold text-white mb-1">User Feedback Inbox</h2>
                        <p className="text-gray-500 text-sm">Direct line from our community.</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-8">
                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {suggestions.map(sug => (
                               <div key={sug.id} className={`p-6 rounded-2xl border transition-all ${sug.status === 'new' ? 'bg-gray-900 border-wedding-900/50 shadow-2xl' : 'bg-gray-800 border-gray-700 opacity-60'}`}>
                                  <div className="flex justify-between items-start mb-4">
                                     <h4 className="text-white font-bold text-sm">{sug.userName} <span className="text-gray-500 font-normal">({sug.userRole})</span></h4>
                                     {sug.status === 'new' && <span className="bg-wedding-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded">New</span>}
                                  </div>
                                  <p className="text-gray-300 text-sm italic mb-6 leading-relaxed">"{sug.content}"</p>
                                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-700/50">
                                     <span className="text-gray-500 text-[10px] font-mono">{new Date(sug.timestamp).toLocaleDateString()}</span>
                                     <button 
                                        onClick={() => onUpdateSuggestionStatus(sug.id, sug.status === 'new' ? 'reviewed' : 'new')}
                                        className={`p-2 rounded-lg transition-all ${sug.status === 'new' ? 'text-wedding-500 hover:bg-wedding-900/30' : 'text-gray-400 hover:bg-gray-700'}`}
                                     >
                                        <CheckCircle2 className="w-5 h-5" />
                                     </button>
                                  </div>
                               </div>
                            ))}
                       </div>
                    </div>
                </div>
            )}

            {activeTab === 'settings' && (
                <div className="space-y-8 animate-fadeIn">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Global Service Catalog */}
                        <div className="bg-gray-800 border border-gray-700 p-8 rounded-2xl shadow-xl flex flex-col">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <List className="w-5 h-5 mr-3 text-blue-500" />
                                Global Service Catalog
                            </h3>
                            <div className="flex-1 space-y-6">
                               <form onSubmit={handleAddService} className="flex gap-3">
                                  <input 
                                     type="text" 
                                     value={newServiceName}
                                     onChange={(e) => setNewServiceName(e.target.value)}
                                     placeholder="e.g. Floral Design"
                                     className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                  />
                                  <button type="submit" className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-lg">
                                     <Plus className="w-5 h-5" />
                                  </button>
                               </form>
                               
                               <div className="bg-gray-900/50 rounded-xl border border-gray-700 p-4 max-h-[250px] overflow-y-auto custom-scrollbar">
                                  <div className="flex flex-wrap gap-2">
                                     {globalServices.map((service, i) => (
                                        <span key={i} className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-bold text-gray-300 flex items-center">
                                           <CheckCircle2 className="w-3 h-3 mr-2 text-green-500" />
                                           {service}
                                        </span>
                                     ))}
                                  </div>
                               </div>
                               <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest text-center mt-2">
                                  Total Active Categories: {globalServices.length}
                               </p>
                            </div>
                        </div>

                        {/* Payout Config */}
                        <div className="bg-gray-800 border border-gray-700 p-8 rounded-2xl shadow-xl">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <DollarSign className="w-5 h-5 mr-3 text-green-500" />
                                Platform Payouts & Fees
                            </h3>
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-900 rounded-xl border border-gray-700">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Business Sub ($/mo)</label>
                                        <input 
                                            type="number" 
                                            className="bg-transparent text-xl font-bold text-white outline-none w-full"
                                            value={globalPricing.business}
                                            onChange={(e) => setGlobalPricing({...globalPricing, business: Number(e.target.value)})}
                                        />
                                    </div>
                                    <div className="p-4 bg-gray-900 rounded-xl border border-gray-700">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Pro Individual ($)</label>
                                        <input 
                                            type="number" 
                                            className="bg-transparent text-xl font-bold text-white outline-none w-full"
                                            value={globalPricing.individual}
                                            onChange={(e) => setGlobalPricing({...globalPricing, individual: Number(e.target.value)})}
                                        />
                                    </div>
                                </div>
                                <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-xl flex items-start">
                                   <RefreshCw className="w-5 h-5 mr-3 text-blue-500 mt-1" />
                                   <p className="text-xs text-gray-400 leading-relaxed">
                                      Price updates propagate globally across all currencies. Existing subscriptions remain grandfathered.
                                   </p>
                                </div>
                                <button onClick={handleCommitPricing} className="w-full py-4 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-xl transition-all flex items-center justify-center">
                                    <Save className="w-5 h-5 mr-2" />
                                    Commit Pricing Model
                                </button>
                            </div>
                        </div>

                        {/* Global Broadcast */}
                        <div className="bg-gray-800 border border-gray-700 p-8 rounded-2xl shadow-xl lg:col-span-2">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <BellRing className="w-5 h-5 mr-3 text-wedding-500" />
                                Global Broadcast
                            </h3>
                            <form onSubmit={handleBroadcast} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Message Content</label>
                                    <textarea 
                                        className="w-full p-4 bg-gray-900 border border-gray-700 rounded-xl focus:ring-2 focus:ring-wedding-500 outline-none text-sm min-h-[120px]"
                                        placeholder="Attention all users..."
                                        value={broadcastMessage}
                                        onChange={(e) => setBroadcastMessage(e.target.value)}
                                    />
                                </div>
                                <button className="w-full py-4 bg-wedding-600 hover:bg-wedding-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center">
                                    <Send className="w-5 h-5 mr-2" />
                                    Push Notification to All Users
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </main>
      </div>
    </div>
  );
};

export default OwnerPortal;
